import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  handleReset = () => {
    // pushState changes the URL without a reload (preserves in-memory auth tokens).
    // Dispatching popstate tells React Router to re-evaluate the current route.
    window.history.pushState(null, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
    this.setState({ hasError: false });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center"
        style={{ background: 'var(--color-bg)', padding: '24px' }}
      >
        <div style={{ textAlign: 'center', maxWidth: 440 }}>
          <div style={{
            width: 72, height: 72, borderRadius: 18,
            background: '#FEE2E2',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <span style={{ fontSize: 32 }}>⚠️</span>
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 900, color: 'var(--brand-navy)', marginBottom: 8 }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: 14.5, color: 'var(--color-text-muted)', lineHeight: 1.6, marginBottom: 24 }}>
            An unexpected error occurred. Try reloading the page.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '9px 18px', borderRadius: 9,
                border: '1px solid var(--color-border)',
                background: 'var(--color-surface)',
                fontSize: 14, fontWeight: 600, color: 'var(--color-text)',
                cursor: 'pointer',
              }}
            >
              <RefreshCw style={{ width: 14, height: 14 }} />
              Reload
            </button>
            <button
              type="button"
              onClick={this.handleReset}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '9px 18px', borderRadius: 9,
                border: 'none',
                background: 'var(--btn-primary-bg)',
                fontSize: 14, fontWeight: 700, color: '#fff',
                cursor: 'pointer',
              }}
            >
              <Home style={{ width: 14, height: 14 }} />
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
